export type ThoughtBlock =
  /** Regular reading paragraph */
  | { type: "p"; text: string }
  | { type: "h2"; text: string }
  /** Short line set larger to give the reader a beat */
  | { type: "statement"; text: string }
  /** Pull quote in dusty blue */
  | { type: "pull"; text: string }
  /** Key idea in a solid dusty blue block */
  | { type: "callout"; text: string }
  /** One framing turning into another */
  | { type: "contrast"; from: { label: string; text: string }; to: { label: string; text: string } }
  /** A set of questions in a dark panel */
  | { type: "questions"; title: string; items: string[] }
  /** Short lines stacked for rhythm; an optional lead-in sits above each */
  | { type: "stack"; items: { lead?: string; text: string }[] };

export type Thought = {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  /** e.g. "Coming soon" — shown in place of read time until published */
  status?: string;
  readTime?: string;
  image?: string;
  /** Empty until the full article is written */
  body: ThoughtBlock[];
};

export const thoughts: Thought[] = [
  {
    slug: "progress-should-still-feel-human",
    title: "Progress should still feel human.",
    excerpt:
      "AI is forcing organizations to make decisions faster than they can fully understand their consequences. Maybe leadership in this moment isn’t about having all the answers. Maybe it’s about getting better at the questions.",
    category: "AI + Humanity",
    date: "October 2026",
    readTime: "9 min read",
    // Remove this line to publish: the meta switches to the read time, the
    // "Coming soon" pill becomes "Read the thought" and the article body shows.
    status: "Coming soon",
    body: [
      { type: "p", text: "I’ve spent most of my career trying to make technology work better." },
      {
        type: "p",
        text: "I started as an English major writing content in an era when SEO meant something very different than it does today. Eventually, writing the words wasn’t enough. I wanted to change the landing page they lived on, so I learned enough HTML and CSS to do it. That curiosity kept expanding into analytics, advertising, websites, marketing technology, user experience, digital strategy and web product.",
      },
      { type: "statement", text: "Technology changed. I kept learning." },
      { type: "p", text: "That has generally been the fun part." },
      { type: "statement", text: "Then AI came along." },
      {
        type: "p",
        text: "Like a lot of people who work in digital, I jumped in. I experimented. I found things it could make faster. I watched it do things that would have seemed impossible not very long ago.",
      },
      { type: "p", text: "I still think that’s incredible." },
      { type: "p", text: "But somewhere along the way, the question I was asking changed." },
      {
        type: "contrast",
        from: { label: "It stopped being only", text: "What can this technology do?" },
        to: { label: "And became", text: "What happens to people when we do it?" },
      },
      { type: "p", text: "I think that second question is going to define a lot of the next decade." },

      { type: "h2", text: "I’m a technology optimist with questions" },
      { type: "p", text: "I believe technology can make people’s lives better." },
      { type: "p", text: "I also think optimism without curiosity can become its own kind of complacency." },
      {
        type: "p",
        text: "We talk about AI in terms of efficiency, productivity, scale, personalization and innovation. Those things matter. But technology doesn’t exist in a little technology bubble.",
      },
      {
        type: "statement",
        text: "It exists in people’s jobs. Their classrooms. Their relationships. Their creative work. Their decisions. Their communities.",
      },
      { type: "p", text: "And increasingly, it exists in the infrastructure around them." },
      {
        type: "p",
        text: "My own thinking about AI changed when its physical footprint became much more tangible to me. Conversations about data centers, energy, water, land and infrastructure made something that can feel almost magical on a screen suddenly feel very physical.",
      },
      { type: "p", text: "Of course it is physical." },
      {
        type: "stack",
        items: [
          { text: "Every prompt goes somewhere." },
          { text: "Every model requires infrastructure." },
          {
            text: "Every technological advancement has a cost somewhere, even when the person benefiting from it never sees that cost directly.",
          },
        ],
      },
      { type: "p", text: "That sent me down a rabbit hole." },
      {
        type: "p",
        text: "I started reading more about AI and sustainability. That led me deeper into questions about ethics, accessibility, transparency, consent, human agency and what happens to people’s sense of worth as machines become capable of doing more of the things we once considered distinctly human.",
      },
      {
        type: "p",
        text: "The more I learned, the less interested I became in deciding whether I was “pro-AI” or “anti-AI.”",
      },
      { type: "pull", text: "I’m interested in the gray." },

      { type: "h2", text: "“Can we?” is not the same question as “Should we?”" },
      { type: "p", text: "Organizations are under enormous pressure to do something with AI." },
      {
        type: "p",
        text: "Leaders don’t want to fall behind. Employees are already experimenting. Vendors are adding AI to nearly everything. Boards are asking about it. Customers are encountering it whether they realize it or not.",
      },
      { type: "p", text: "So the conversation can move very quickly from:" },
      {
        type: "contrast",
        from: { label: "From", text: "AI can do this." },
        to: { label: "To", text: "How quickly can we implement it?" },
      },
      { type: "p", text: "There’s a question missing in the middle." },
      { type: "statement", text: "Should we?" },
      { type: "p", text: "And I don’t mean that rhetorically." },
      { type: "p", text: "Sometimes the answer is absolutely yes." },
      {
        type: "p",
        text: "AI can make information more accessible. It can reduce repetitive work. It can help people analyze information, communicate, create and learn. It can give someone a starting point when the blank page is the hardest part. It can help small teams do things that once required resources they simply didn’t have.",
      },
      { type: "p", text: "I use it. I work with it. I’m excited by parts of it." },
      { type: "p", text: "But “useful” and “justified” are not synonyms." },
      {
        type: "stack",
        items: [
          { text: "A technology can make something faster and still make the overall experience worse." },
          { text: "It can reduce labor while increasing environmental cost." },
          { text: "It can personalize an experience while reducing privacy." },
          { text: "It can answer a question while making it harder to know where the answer came from." },
          { text: "It can automate a task while unintentionally removing something meaningful from someone’s work." },
        ],
      },
      { type: "callout", text: "AI can be useful. Useful isn’t the same thing as justified." },
      { type: "p", text: "That distinction matters." },

      { type: "h2", text: "Responsible AI starts before the AI" },
      { type: "p", text: "“Responsible AI” is becoming a popular phrase." },
      {
        type: "p",
        text: "I worry it could become one of those phrases organizations repeat so often that it eventually stops meaning much of anything.",
      },
      {
        type: "p",
        text: "A responsible AI strategy isn’t responsible because the word appears in a framework. It’s responsible because of the decisions people make.",
      },
      { type: "p", text: "And I think those decisions should start before anyone chooses a tool." },
      {
        type: "questions",
        title: "Before anyone chooses a tool",
        items: [
          "What problem are we actually trying to solve?",
          "Does AI need to be part of the solution?",
          "Who will be affected?",
          "What should they know?",
          "What data is involved?",
          "Should they have a choice?",
          "Is the experience accessible?",
          "What are the environmental implications?",
          "What happens when the technology gets something wrong?",
          "Where does human judgment need to remain?",
          "Who is accountable?",
          "What evidence would make us expand its use?",
          "And what would make us stop?",
        ],
      },
      { type: "p", text: "Those questions are less exciting than a product demo." },
      { type: "statement", text: "They’re also strategy." },
      {
        type: "p",
        text: "In fact, I increasingly think the organizations that navigate AI well won’t necessarily be the ones that adopt the most technology.",
      },
      {
        type: "pull",
        text: "They’ll be the ones that become very good at deciding which technology deserves to exist in which parts of their organization, and why.",
      },

      { type: "h2", text: "The human behavior part matters more than I expected" },
      {
        type: "p",
        text: "There’s another part of my life that has been quietly changing the way I think about all of this.",
      },
      { type: "statement", text: "I’m studying clinical mental health counseling." },
      {
        type: "p",
        text: "On paper, counseling and digital strategy can look like two unrelated directions. They don’t feel unrelated to me anymore.",
      },
      {
        type: "p",
        text: "Technology is built for human beings. It increasingly attempts to predict human beings. It influences our behavior, our attention, our decisions and sometimes how we understand ourselves and one another.",
      },
      {
        type: "p",
        text: "So understanding people seems like a pretty useful thing for a technologist to care about.",
      },
      { type: "statement", text: "And people are wonderfully inconvenient." },
      {
        type: "p",
        text: "We are emotional and rational. Independent and deeply social. Predictable in some ways and completely surprising in others.",
      },
      {
        type: "p",
        text: "We need efficiency sometimes. We also need agency. Meaning. Connection. Trust. The feeling that our contribution matters.",
      },
      {
        type: "p",
        text: "Those things are harder to put on a dashboard. That doesn’t make them less real.",
      },
      { type: "p", text: "Studying human behavior hasn’t made me less interested in technology." },
      { type: "pull", text: "It has made me less naive about it." },
      { type: "p", text: "I think that’s a good thing." },

      { type: "h2", text: "Leadership might mean getting comfortable with “I don’t know yet”" },
      {
        type: "p",
        text: "AI presents an uncomfortable leadership problem because the technology is moving faster than our ability to understand all of its consequences.",
      },
      { type: "p", text: "And leaders are generally rewarded for having answers." },
      {
        type: "stack",
        items: [
          { text: "What’s the strategy?" },
          { text: "What’s the policy?" },
          { text: "Which tools are approved?" },
          { text: "What are our competitors doing?" },
          { text: "What are we going to do?" },
        ],
      },
      { type: "p", text: "There is pressure to turn uncertainty into certainty as quickly as possible." },
      {
        type: "p",
        text: "But I don’t think responsible leadership requires pretending we know things we don’t.",
      },
      {
        type: "stack",
        items: [
          { lead: "Sometimes leadership means saying:", text: "We know enough to move forward." },
          { lead: "Sometimes it means:", text: "We need to experiment before we decide." },
          { lead: "Sometimes:", text: "We need to hear from the people who will actually live with this." },
          { lead: "And sometimes:", text: "Yes, we could do this. We’re choosing not to." },
        ],
      },
      { type: "p", text: "That isn’t resistance to innovation." },
      { type: "statement", text: "That’s judgment." },
      {
        type: "p",
        text: "The more powerful our technology becomes, the more important that judgment becomes too.",
      },

      { type: "h2", text: "Compassion and ambition belong in the same room" },
      { type: "p", text: "I care about progress." },
      {
        type: "p",
        text: "I like ambitious people. I like big ideas. I like figuring out how to make something better than it was yesterday.",
      },
      {
        type: "p",
        text: "But I’ve also come to believe that the best leaders have just as much compassion as ambition. That matters in technology.",
      },
      {
        type: "p",
        text: "We should be ambitious enough to imagine what new tools make possible. And compassionate enough to think seriously about the people who experience the consequences.",
      },
      {
        type: "stack",
        items: [
          {
            text: "We should care about efficiency and about whether someone feels devalued by the way we achieved it.",
          },
          { text: "We should care about innovation and about accessibility." },
          { text: "We should care about capability and about consent." },
          {
            text: "We should care about what technology gives us and what it asks from the physical world in return.",
          },
        ],
      },
      { type: "p", text: "Those ideas don’t have to compete." },
      { type: "pull", text: "In fact, I think holding them together is the work." },

      { type: "h2", text: "Progress should still feel human" },
      { type: "p", text: "I don’t know exactly what AI will become. Neither does anyone else." },
      {
        type: "p",
        text: "That uncertainty doesn’t mean we should freeze. And it doesn’t mean we should race ahead without looking around.",
      },
      {
        type: "p",
        text: "It means we have an opportunity to get better at navigating uncertainty itself.",
      },
      {
        type: "stack",
        items: [
          { text: "Learn." },
          { text: "Experiment." },
          { text: "Question." },
          { text: "Measure." },
          { text: "Listen." },
          { text: "Change our minds when the evidence changes." },
          { text: "Use AI when it genuinely makes something better." },
          { text: "Leave it out when it doesn’t." },
        ],
      },
      {
        type: "p",
        text: "And resist the idea that technological progress is somehow inevitable and our only job is to accommodate it.",
      },
      {
        type: "p",
        text: "Humans are still making choices here. That means we still have responsibility for them.",
      },
      {
        type: "p",
        text: "Maybe leadership in the age of AI isn’t about predicting exactly where the technology is going.",
      },
      {
        type: "p",
        text: "Maybe it’s about developing enough technical understanding to recognize what’s possible, enough curiosity to keep learning and enough humanity to remember that possibility was never the only thing that mattered.",
      },
      { type: "p", text: "Technology will change. It always does." },
      { type: "callout", text: "People are still the point." },
      {
        type: "p",
        text: "And whatever comes next, I want to help build a version of progress that remembers that.",
      },
    ],
  },
];

export function thoughtMeta(thought: Thought) {
  return [thought.date, thought.status ?? thought.readTime].filter(Boolean).join(" · ");
}
